<template>
  <div class="w-full pr-2">
    <div
      class="content-selector-assets-uploader flex flex-col gap-3 items-center justify-center px-[10px] py-[20px] mb-3 rounded"
      :class="{ 'cursor-pointer': !uploads.length, 'cursor-not-allowed': uploads.length }"
      :style="{ border: `2px dashed ${themeVars.primary5}`, color: themeVars.primary }"
      @click="onUploadClick"
      @dragenter.prevent
      @dragover.prevent
      @drop.prevent="onFileDrop"
    >
      <CIcon v-if="!uploads.length" icon="upload" size="36px" />
      <NSpin v-else :color="themeVars.primary" :size="36" />
      <div>
        {{
          uploads.length
            ? t('canvasUI.contentSelector.assetUploader.uploadingFiles')
            : t('canvasUI.contentSelector.assetUploader.clickOrDropFilesToUpload')
        }}
      </div>
      <div v-if="uploads.length" class="text-center" :style="{ color: themeVars.text3 }">
        <div v-for="upload in uploads" :key="upload.id">{{ upload.name }}: {{ upload.progress }}%</div>
      </div>
      <input ref="fileInput" :accept="acceptedFileTypes" class="hidden" multiple type="file" @change="onFileChange" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { NSpin } from 'naive-ui'
import { type AxiosProgressEvent } from 'axios'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { useAssetUpload } from '@lib/composables/useAssetUpload'
import {
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_VIDEO_TYPES,
  SUPPORTED_IMAGE_EXTENSIONS,
  SUPPORTED_VIDEO_EXTENSIONS,
} from '@lib/constants/uploads.const'

const emit = defineEmits<{ uploadsComplete: [] }>()
const { uploadAsset } = useAssetUpload()
const themeVars = useThemeVars()
const fileInput = ref<HTMLInputElement | null>(null)
const { t } = useI18n()
const uploads = ref<{ id: number; name: string; progress: number; file: File }[]>([])

const acceptedFileTypes = [...SUPPORTED_IMAGE_EXTENSIONS, ...SUPPORTED_VIDEO_EXTENSIONS].join(',')

function onUploadClick() {
  if (!uploads.value.length) fileInput.value?.click()
}

async function uploadFiles(files: FileList) {
  if (uploads.value.length) return

  const toUpload = Array.from(files).filter((file) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    return SUPPORTED_IMAGE_TYPES.includes(ext) || SUPPORTED_VIDEO_TYPES.includes(ext)
  })
  if (!toUpload.length) return

  uploads.value = toUpload.map((file, index) => ({
    id: index,
    file,
    name: file.name,
    progress: 0,
  }))

  const onUploadProgress = (progressEvent: AxiosProgressEvent, file: File) => {
    const total = progressEvent.total || progressEvent.loaded
    const progress = Math.round((progressEvent.loaded * 100) / total)
    const upload = uploads.value.find((u) => u.file === file)
    if (upload) {
      upload.progress = progress
    }
  }

  await Promise.all(
    uploads.value.map((upload) =>
      uploadAsset(upload.file, onUploadProgress)
        .then(() => ({ success: true }))
        .catch((error) => {
          console.error(`Error uploading file ${upload.name}:`, error)
          return { success: false }
        }),
    ),
  )
  emit('uploadsComplete')
  uploads.value = []
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  if (input.files) {
    uploadFiles(input.files)
  }
}

function onFileDrop(event: DragEvent) {
  if (event.dataTransfer?.files) {
    uploadFiles(event.dataTransfer.files)
  }
}
</script>
